import { FunctionComponent, useState } from 'react';
import { Avatar, Label, DescriptionReviewDialog } from 'src/shared';
import { ReactComponent as GithubIcon } from 'src/assets/icons/github-icon.svg';
import { ReactComponent as PlusIcon } from 'src/assets/icons/plus.svg';
import { ReactComponent as ReviewDescriptionIcon } from 'src/assets/icons/review-description.svg';
import { IconButton, Tooltip } from '@mui/material';

import theme from 'src/theme';
import {
    S,
    Root,
    LabelWrapper,
    AvatarWithText,
    RepoLinkWrapper,
    TooltipBg,
    RepoName,
    HeaderIconRow,
    ReviewDescriptionIconWrapper
} from './styled';
import BillingButton from '../BillingButton';
import { ClientSelectors } from 'src/redux/client/selectors';
import { TooltipWrapper } from 'src/shared/Table/styled';
import { useSelector } from 'react-redux';
import { getProject } from 'src/redux/projects/selectors';
import { useAppDispatch } from 'src/hooks';
import { getGithubAppUrl, updateProject } from 'src/redux/onboarding/service';
import { getProjectDetails } from 'src/redux/projects/service';
import { UpdateProjectRequest } from 'src/types/projects';

type Props = {};

const ClientHeader: FunctionComponent<Props> = ({}: Props) => {
    const dispatch = useAppDispatch();
    const { selectedProjectId, isLoading, repositories } = ClientSelectors();
    const selectedProject = useSelector(getProject(selectedProjectId));
    const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);

    const handleAddRepository = async () => {
        try {
            if (selectedProjectId) {
                const response = await dispatch(
                    getGithubAppUrl({ projectId: selectedProjectId })
                ).unwrap();
                if (response.githubAppUrl) {
                    window.open(response.githubAppUrl, '_blank');
                }
            }
        } catch (error) {
            console.error('Failed to get GitHub auth URL:', error);
        }
    };

    const handleSaveDescription = async (
        newDescription: string,
        attachmentList: number[]
    ) => {
        if (!selectedProjectId) return;

        try {
            const updateRequest: UpdateProjectRequest = {
                projectId: parseInt(selectedProjectId),
                description: newDescription,
                attachmentList: attachmentList
            };
            await dispatch(updateProject(updateRequest)).unwrap();
            await dispatch(getProjectDetails(selectedProjectId)).unwrap();
            setDescriptionDialogOpen(false);
        } catch (error) {
            console.error('Failed to update project description:', error);
            throw error;
        }
    };

    const hasProjectDetails = Boolean(selectedProjectId && selectedProject);

    return (
        <Root>
            {!isLoading && hasProjectDetails && (
                <>
                    <AvatarWithText>
                        <Avatar
                            background={theme.color.darkBlue}
                            size="110px"
                            image=""
                        />
                        <LabelWrapper>
                            <Label sxOverrides={S.projectTitle}>
                                {selectedProject?.projectName ??
                                    'Selected project'}
                            </Label>
                        </LabelWrapper>
                    </AvatarWithText>
                    <TooltipWrapper
                        title={
                            <TooltipBg>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px',
                                        gap: '8px'
                                    }}
                                >
                                    <Label sxOverrides={S.projectTitle}>
                                        All repositories
                                    </Label>
                                    <Tooltip title="Add new repository">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddRepository();
                                            }}
                                            size="small"
                                            sx={{
                                                color: theme.color
                                                    .lightningYellow,
                                                padding: '4px',
                                                marginTop: '0px',
                                                marginBottom: '0px',
                                                '&:hover': {
                                                    backgroundColor:
                                                        'rgba(255, 220, 168, 0.1)'
                                                }
                                            }}
                                        >
                                            <PlusIcon
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    display: 'block'
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                                {repositories ? (
                                    repositories.map((repo, index) => (
                                        <RepoLinkWrapper
                                            key={index}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                window.open(repo.url, '_blank');
                                            }}
                                        >
                                            <RepoName>{repo.name}</RepoName>
                                        </RepoLinkWrapper>
                                    ))
                                ) : (
                                    <Label sxOverrides={S.noReposLabel}>
                                        No repositories are linked to this
                                        project
                                    </Label>
                                )}
                            </TooltipBg>
                        }
                        arrow
                    >
                        <GithubIcon />
                    </TooltipWrapper>

                    <HeaderIconRow>
                        <Tooltip title="Review project description" arrow>
                            <ReviewDescriptionIconWrapper
                                onClick={() => setDescriptionDialogOpen(true)}
                            >
                                <ReviewDescriptionIcon />
                            </ReviewDescriptionIconWrapper>
                        </Tooltip>
                        {selectedProjectId && <BillingButton />}
                    </HeaderIconRow>
                </>
            )}

            {/* Description Review Dialog */}
            <DescriptionReviewDialog
                open={descriptionDialogOpen}
                onClose={() => setDescriptionDialogOpen(false)}
                description={selectedProject?.description || ''}
                attachments={selectedProject?.attachments || []}
                onSave={handleSaveDescription}
            />
        </Root>
    );
};
export default ClientHeader;
